import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FaCog, FaCoins, FaEuroSign, FaHistory, FaUniversity } from "react-icons/fa";
import { useCan } from "../../authz/useCan";
import {
  LdsOverviewCard,
  LdsOverviewEmpty,
  LdsOverviewGrid,
  LdsOverviewHero,
  LdsOverviewPage,
  LdsOverviewSection,
} from "../../components/lds/LdsOverview";

export default function FinanceOverview(): ReactElement {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const canViewStudents = useCan("finance.reports.students", "view");
  const canSearchBankAccounts = useCan("families.bankaccounts", "search");
  const canViewSettings = useCan("finance.settings", "view");
  const canViewContributions = useCan("finance.contributions", "view");
  const hasVisibleTiles = canViewStudents || canSearchBankAccounts || canViewSettings || canViewContributions;

  return (
    <LdsOverviewPage>
      <LdsOverviewHero
        icon={FaEuroSign}
        eyebrow={t("finance.overview.title", { defaultValue: "Finance" })}
        title={t("finance.overview.title", { defaultValue: "Finance" })}
        description={t("finance.overview.description", {
          defaultValue: "Overviews and lookups for contributions and bank accounts.",
        })}
      />

      {hasVisibleTiles ? (
        <LdsOverviewSection
          title={t("finance.overview.sections.title", { defaultValue: "Available overviews" })}
          description={t("finance.overview.sections.description", { defaultValue: "Choose what you want to open." })}
        >
          <LdsOverviewGrid>
            {canViewContributions || canViewStudents ? (
              <LdsOverviewCard
                icon={FaCoins}
                eyebrow={t("finance.overview.cards.contributions.category", { defaultValue: "Parent contribution" })}
                title={t("finance.contributions.title", { defaultValue: "Parent contributions" })}
                description={t("finance.overview.cards.contributions.description", {
                  defaultValue: "Per family or per child: what was due, what came in, and the parents' details.",
                })}
                actionLabel={t("common.open", { defaultValue: "Open" })}
                onClick={() => navigate("/finance/payments")}
                animationIndex={0}
              />
            ) : null}
            {canSearchBankAccounts ? (
              <LdsOverviewCard
                icon={FaUniversity}
                eyebrow={t("tools.overview.cards.bankAccountLookup.category", { defaultValue: "Finance" })}
                title={t("tools.overview.cards.bankAccountLookup.title", { defaultValue: "Bank account lookup" })}
                description={t("tools.overview.cards.bankAccountLookup.description", {
                  defaultValue: "Find families or parents by bank account number.",
                })}
                actionLabel={t("common.open", { defaultValue: "Open" })}
                onClick={() => navigate("/finance/bank-account-lookup")}
                animationIndex={1}
              />
            ) : null}
            {canViewSettings ? (
              <LdsOverviewCard
                icon={FaCog}
                eyebrow={t("finance.overview.cards.settings.category", { defaultValue: "Settings" })}
                title={t("finance.settings.title", { defaultValue: "e-Boekhouden settings" })}
                description={t("finance.overview.cards.settings.description", {
                  defaultValue: "Set up and test the connection with e-Boekhouden.",
                })}
                actionLabel={t("common.open", { defaultValue: "Open" })}
                onClick={() => navigate("/finance/settings")}
                animationIndex={2}
              />
            ) : null}
            {canViewContributions || canViewSettings ? (
              <LdsOverviewCard
                icon={FaHistory}
                eyebrow={t("finance.overview.cards.import_log.category", { defaultValue: "e-Boekhouden" })}
                title={t("finance.import_log.title", { defaultValue: "Connection log" })}
                description={t("finance.overview.cards.import_log.description", {
                  defaultValue: "When payments were fetched, whether it worked and how many there were.",
                })}
                actionLabel={t("common.open", { defaultValue: "Open" })}
                onClick={() => navigate("/finance/imports")}
                animationIndex={3}
              />
            ) : null}
          </LdsOverviewGrid>
        </LdsOverviewSection>
      ) : (
        <LdsOverviewEmpty>
          {t("finance.overview.empty", { defaultValue: "No finance overviews are available with your current permissions." })}
        </LdsOverviewEmpty>
      )}
    </LdsOverviewPage>
  );
}
